import React, { useEffect, useState } from "react";
import { Edit, Save, X, Plus, RotateCw } from "lucide-react";
import AdminCardActions from "../components/AdminCardActions";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    fetchServices,
    fetchNetworks,
    updateService,
    createService
} from "../services/services";

const initialService = {
    name: "",
    descriptions: "",
    feature_key: "",
    status: true,
    network_name: "",
};

const featureOptions = [
    { value: "", label: "Без спеціальної функції" },
    { value: "sensors", label: "Сенсори" },
];

const ServicesAdmins = () => {
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [networks, setNetworks] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editedData, setEditedData] = useState(initialService);
    const [originalData, setOriginalData] = useState(initialService);
    const [newService, setNewService] = useState(initialService);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        loadServices();
        loadNetworks();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = services.filter((s) =>
            (s.name || "").toString().toLowerCase().includes(q) ||
            (s.descriptions || "").toString().toLowerCase().includes(q) ||
            (s.feature_key || "").toString().toLowerCase().includes(q) ||
            (s.network_name || "").toString().toLowerCase().includes(q)
        );
        setFilteredServices(filtered);
    }, [services, searchQuery]);

    const loadServices = async () => {
        const data = await fetchServices();
        setServices(data);
    };

    const loadNetworks = async () => {
        const data = await fetchNetworks();
        setNetworks(data);
    };

    const handleFieldChange = (setter) => (e) => {
        const { name, value, type } = e.target;
        const parsedValue = type === "number"
            ? parseFloat(value)
            : value === "true"
                ? true
                : value === "false"
                    ? false
                    : value;

        setter((prev) => ({ ...prev, [name]: parsedValue }));
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setEditedData(item);
        setOriginalData(item);
    };

    const handleCancelEdit = () => {
        setEditedData(originalData);
        setEditingId(null);
    };

    const handleSave = async (id) => {
        try {
            await updateService(id, editedData);
            setEditingId(null);
            loadServices();
        } catch (error) {
            alert(error.message || "Не вдалося оновити сервіс");
        }
    };

    const handleAdd = async () => {
        if (!newService.name.trim()) {
            alert("Назва не може бути порожньою!");
            return;
        }
        try {
            await createService(newService);
            setNewService(initialService);
            setShowAddModal(false);
            loadServices();
        } catch (error) {
            alert(error.message || "Не вдалося додати сервіс");
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredServices) return [];
        const sorted = [...filteredServices];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const statusOptions = [
        { value: true, label: "Активний" },
        { value: false, label: "Неактивний" }
    ];

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadServices} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук сервісів/опису/мережі..."
                    />
                </div>
            </div>

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати сервіс</h3>
                        <input
                            name="name"
                            placeholder="Назва"
                            value={newService.name}
                            onChange={handleFieldChange(setNewService)}
                        />
                        <input
                            name="descriptions"
                            placeholder="Опис"
                            value={newService.descriptions}
                            onChange={handleFieldChange(setNewService)}
                        />
                        <select
                            name="feature_key"
                            value={newService.feature_key}
                            onChange={handleFieldChange(setNewService)}
                        >
                            {featureOptions.map((opt) => (
                                <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <select
                            name="network_name"
                            value={newService.network_name}
                            onChange={handleFieldChange(setNewService)}
                        >
                            <option value="">Без мережі</option>
                            {networks.filter(n => n.status).map(n => (
                                <option key={n.id} value={n.network_name}>{n.network_name}</option>
                            ))}
                        </select>
                        <select
                            name="status"
                            value={newService.status}
                            onChange={handleFieldChange(setNewService)}
                        >
                            {statusOptions.map(opt => (
                                <option key={opt.value.toString()} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid">
                    {filteredServices.map((service) => (
                        <div key={service.id} className="config-card">
                            <div><strong>Назва:</strong> {editingId === service.id ? (
                                <input
                                    name="name"
                                    value={editedData.name}
                                    onChange={handleFieldChange(setEditedData)}
                                />
                            ) : (
                                <span>{service.name}</span>
                            )}</div>

                            <div><strong>Опис:</strong> {editingId === service.id ? (
                                <input
                                    name="descriptions"
                                    value={editedData.descriptions}
                                    onChange={handleFieldChange(setEditedData)}
                                />
                            ) : (
                                <span>{service.descriptions}</span>
                            )}</div>

                            <div><strong>Функція:</strong> {editingId === service.id ? (
                                <select
                                    name="feature_key"
                                    value={editedData.feature_key || ""}
                                    onChange={handleFieldChange(setEditedData)}
                                >
                                    {featureOptions.map((opt) => (
                                        <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            ) : (
                                <span>{featureOptions.find((opt) => opt.value === (service.feature_key || ""))?.label || "Без спеціальної функції"}</span>
                            )}</div>

                            <div><strong>Статус:</strong> {editingId === service.id ? (
                                <select
                                    name="status"
                                    value={editedData.status}
                                    onChange={handleFieldChange(setEditedData)}
                                >
                                    {statusOptions.map(opt => (
                                        <option key={opt.value.toString()} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span>{service.status ? "Активний" : "Неактивний"}</span>
                            )}</div>

                            <div><strong>Мережа:</strong> {editingId === service.id ? (
                                <select
                                    name="network_name"
                                    value={editedData.network_name}
                                    onChange={handleFieldChange(setEditedData)}
                                >
                                    <option value="">Без мережі</option>
                                    {networks.filter(n => n.status).map(n => (
                                        <option key={n.id} value={n.network_name}>
                                            {n.network_name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span>{service.network_name || "Без мережі"}</span>
                            )}</div>

                            <AdminCardActions
                                editing={editingId === service.id}
                                onEdit={() => handleEdit(service)}
                                onSave={() => handleSave(service.id)}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                                    Назва {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("feature_key")}>
                                    Функція {sortBy === "feature_key" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("network_name")}>
                                    Мережа {sortBy === "network_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((service) => (
                                <tr key={service.id}>
                                    <td>
                                        {editingId === service.id ? (
                                            <input
                                                name="name"
                                                value={editedData.name}
                                                onChange={handleFieldChange(setEditedData)}
                                            />
                                        ) : service.name}
                                    </td>
                                    <td>
                                        {editingId === service.id ? (
                                            <input
                                                name="descriptions"
                                                value={editedData.descriptions}
                                                onChange={handleFieldChange(setEditedData)}
                                            />
                                        ) : service.descriptions}
                                    </td>
                                    <td>
                                        {editingId === service.id ? (
                                            <select
                                                name="feature_key"
                                                value={editedData.feature_key || ""}
                                                onChange={handleFieldChange(setEditedData)}
                                            >
                                                {featureOptions.map((opt) => (
                                                    <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        ) : featureOptions.find((opt) => opt.value === (service.feature_key || ""))?.label || "Без спеціальної функції"}
                                    </td>
                                    <td>
                                        {editingId === service.id ? (
                                            <select
                                                name="status"
                                                value={editedData.status}
                                                onChange={handleFieldChange(setEditedData)}
                                            >
                                                {statusOptions.map(opt => (
                                                    <option key={opt.value.toString()} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        ) : service.status ? "Активний" : "Неактивний"}
                                    </td>
                                    <td>
                                        {editingId === service.id ? (
                                            <select
                                                name="network_name"
                                                value={editedData.network_name}
                                                onChange={handleFieldChange(setEditedData)}
                                            >
                                                <option value="">Без мережі</option>
                                                {networks.filter(n => n.status).map(n => (
                                                    <option key={n.id} value={n.network_name}>{n.network_name}</option>
                                                ))}
                                            </select>
                                        ) : service.network_name || "Без мережі"}
                                    </td>
                                    <td className="actions">
                                        {editingId === service.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(service.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancelEdit}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="icon-button"
                                                onClick={() => handleEdit(service)}
                                                title="Редагувати"
                                            >
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ServicesAdmins;